
import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import {
  IChannelData,
  IClientExtradataData,
  ICountryConfig,
  ICurrencyData,
  ISolCustomerCommodity,
  IUserClientData,
  IUserClientSubclientData,
  IUserClientSubclientServicepointData,
  IUserDetailData,
} from "@/entities";
import Multiselect from "vue-multiselect";
import { MODULE, SERVICEPOINT_TYPE } from "@/constans";
import { ValidationObserver } from "vee-validate";
import { SweetAlertResult } from "sweetalert2";
import { Locale } from "@/store";
import { ICashDeviceManagerWrapper, IContextInformation, ITransactions } from "@/services";
import DepositHeader from "@/components/deposit/DepositHeader.vue";

@Component({
  name: "deposit-page",
  components: {
    DepositHeader,
    ValidationObserver,
    Multiselect,
  },
})
export default class DepositPage extends Vue {
  @Getter("getLocale") locale!: Locale;
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig;

  clientSelected: IUserClientData | null = null;
  subclientSelected: IUserClientSubclientData | null = null;
  servicepointSelected: IUserClientSubclientServicepointData | null = null;
  channelSelected: IChannelData | null = null;
  amount: number | null = null;
  currencySelected?: ICurrencyData | null = null;
  comments = "";
  barcode = "";
  commodities: ISolCustomerCommodity[] | [] = [];
  commoditySelected: ISolCustomerCommodity | null = null;
  accountDateSelected: Date | null = new Date();
  minDate: Date | null = null;
  maxDate: Date | null = null;
  enableComments: boolean | undefined = false;
  enableChannel: boolean | undefined = false;
  extraValue: string[] = [];
  @Watch("servicepointSelected")
  onServicepointSelectedChange(newVal: IUserClientSubclientServicepointData | null): void {
    if (newVal) {
      this.enableComments = this.checkEnableComments(newVal);
      this.enableChannel = this.checkEnableChannel(newVal);
    }
  }

  checkEnableComments(servicepoint: IUserClientSubclientServicepointData): boolean | undefined {
    const hasEnableChannel = servicepoint.openChannel;
    const hasChannels = servicepoint.channels && servicepoint.channels.length > 0;
    return hasEnableChannel && !hasChannels;
  }

  checkEnableChannel(servicepoint: IUserClientSubclientServicepointData): boolean | undefined {
    const hasEnableChannel = servicepoint.openChannel;
    const hasChannels = servicepoint.channels && servicepoint.channels.length > 0;
    return (hasEnableChannel && hasChannels) || (!hasEnableChannel && hasChannels);
  }

  $refs!: {
    formDeposit: InstanceType<typeof ValidationObserver>;
  };

  async mounted(): Promise<void> {
    this.subclientSelected = this.clientSelected?.subclients
      ? this.filterSubclientss(this.clientSelected?.subclients)[0]
      : null;
    this.servicepointSelected = this.servicepoints[0] || null;

    this.comments = "";
    this.barcode = "";
    await this.getCommodity();
    const min = new Date();
    min.setDate(min.getDate() - 10);
    const max = new Date();
    max.setDate(max.getDate() + 10);
    this.minDate = min;
    this.maxDate = max;
  }

  async getCommodity(): Promise<void> {
    if (this.getLoggedUser?.country?.code) {
      const res = await this.$services.country.getCommodities(this.getLoggedUser?.country?.code);
      this.commodities = res?.commodities || [];
    }
  }

  get extraData(): IClientExtradataData[] {
    return this.clientSelected?.extradata || [];
  }

  @Watch("clientSelected", { immediate: true })
  onClientSelectedChange(): void {
    this.limpiar();
    this.resetExtraValues();
  }

  resetExtraValues(): void {
    this.extraValue = this.extraData.map((extra) => (extra.type === "boolean" ? "false" : ""));
  }

  get clients(): IUserClientData[] | [] {
    if (!this.clientSelected && this.getLoggedUser?.clients?.length) {
      this.clientSelected = this.filterClients(this.getLoggedUser?.clients)[0] || null;
    }
    return this.filterClients(this.getLoggedUser?.clients);
  }

  get subclients(): IUserClientSubclientData[] | [] {
    this.subclientSelected = null;
    return this.filterSubclientss(this.clientSelected?.subclients)
      ? this.filterSubclientss(this.clientSelected?.subclients)
      : [];
  }

  get servicepoints(): IUserClientSubclientServicepointData[] | [] {
    if (
      !this.subclientSelected?.servicepoints?.find(
        (servicepoint) => servicepoint === this.servicepointSelected
      )
    ) {
      this.servicepointSelected = null;
    }

    return this.filterServicepoints(this.subclientSelected?.servicepoints);
  }

  filterClients(clients: IUserClientData[] | undefined): IUserClientData[] {
    return clients?.filter((c) => this.filterSubclientss(c.subclients).length) || [];
  }

  filterSubclientss(
    subclients: IUserClientSubclientData[] | undefined
  ): IUserClientSubclientData[] {
    return subclients?.filter((s) => this.filterServicepoints(s.servicepoints).length) || [];
  }

  filterServicepoints(
    servicepoints: IUserClientSubclientServicepointData[] | undefined
  ): IUserClientSubclientServicepointData[] {
    return (
      servicepoints?.filter((s) => {
        return s?.adapters?.filter((a) => {
          return a.code === MODULE.DEPOSIT.toString() && s?.type == SERVICEPOINT_TYPE.CASHTODAY;
        }).length;
      }) || []
    );
  }

  getFecha(date: Date): string {
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  getHora(date: Date): string {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  getTimestampCompact(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, "0");
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  }

  async sendDeposit(): Promise<void> {
    const { isConfirmed } = await this.modalConfirm();
    if (!isConfirmed) return;

    const cashDeviceManagerWrapper = this.buildCashDeviceManagerWrapper();

    const sendConfirmed = await this.$services.deposit.prmTransactions(cashDeviceManagerWrapper);
    if (sendConfirmed) {
      this.makeToast("success", this.$t("messages.deposit.ok").toString());
      this.limpiar();
    } else {
      this.makeToast("warning", this.$t("messages.deposit.ko").toString());
    }
  }

  private buildCashDeviceManagerWrapper(): ICashDeviceManagerWrapper {
    return {
      cashDeviceManager: {
        contextInformation: this.buildContextInformation(),
        transactions: this.buildTransactions(),
      },
    } as ICashDeviceManagerWrapper;
  }

  buildContextInformation(): IContextInformation {
    const extraDataObj: Record<string, string> = {
      OBSERVATIONS: this.comments,
    };
    if (this.extraValue?.length) {
      this.extraValue.forEach((value, index) => {
        const extradataItem = this.extraData[index];
        if (value !== "" && extradataItem?.name) {
          extraDataObj[extradataItem.name] = value;
        }
      });
    }
    const dateNow = new Date();
    const deviceId = this.servicepointSelected?.deviceId ?? "";
    const actualId = `${this.getTimestampCompact(dateNow)}_${deviceId}_CASHIN`;
    const hasChannels =
      this.servicepointSelected?.channels && this.servicepointSelected?.channels?.length > 0;

    return {
      countryId: this.getLoggedUser.country?.code || "",
      timeZone: this.getFormattedTimeZone(),
      timestamps: {
        transactionDate: this.getFecha(dateNow),
        transactionTime: this.getHora(dateNow),
        accountDate: this.accountDateSelected
          ? this.getFecha(this.accountDateSelected)
          : this.getFecha(dateNow),
        accountTime: "00:00:00",
      },
      device: {
        deviceType: "ACCEPTOR",
        deviceId: deviceId,
      },
      client: {
        customerName: this.clientSelected
          ? this.clientSelected?.codeSol + " - " + this.clientSelected?.description
          : "",
        clientId: this.clientSelected?.idSmartcash ?? "",
        subClientId: this.subclientSelected?.idSmartcash ?? "",
        servicePointId: this.servicepointSelected?.idSmartcash ?? "",
        userName: this.getLoggedUser.email ?? "",
        cashCenter: this.subclientSelected?.delegation?.idSmartcash ?? "",
      },
      sessionId: {
        actualId: actualId,
      },
      channel: hasChannels ? this.channelSelected?.text : this.comments,
      extraData: extraDataObj,
    } as IContextInformation;
  }

  buildTransactions(): ITransactions {
    return {
      transType: {
        value: "CASHIN",
      },
      total: [
        {
          currency: this.currencySelected?.code ?? "",
          value: (this.amount ? Math.round(this.amount * 100) : 0) + "",
        },
      ],
      transUnits: [
        {
          boxName: "DROPSAFE",
          mix: [
            {
              currency: this.currencySelected?.code ?? "",
              numItems: 1,
              type: this.commoditySelected?.code ?? "AGGR",
              total: this.amount ? Math.round(this.amount * 100) : 0,
              denomination: "0",
              barCode: this.barcode,
            },
          ],
        },
      ],
    } as ITransactions;
  }

  limpiar(): void {
    this.accountDateSelected = new Date();
    this.channelSelected = null;
    this.commoditySelected = null;
    this.amount = null;
    this.currencySelected = null;
    this.comments = "";
    this.barcode = "";
    this.extraValue = [];
    this.$refs.formDeposit?.reset();
  }

  makeToast(variant: string, message: string): void {
    this.$bvToast.toast(message, {
      autoHideDelay: 4000,
      variant,
      toaster: "b-toaster-bottom-center",
      noCloseButton: true,
    });
  }

  async modalConfirm(): Promise<SweetAlertResult> {
    return this.$swal({
      title: this.$t("messages.deposit.confirm.title").toString(),
      text: this.$t("messages.deposit.confirm.description", [
        `${this.amount} ${this.currencySelected?.code || ""}`,
      ]).toString(),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: this.$t("label.yes").toString(),
      cancelButtonText: this.$t("label.no").toString(),
    });
  }

  getFormattedTimeZone(): string {
    const date = new Date();
    const timeZone = Intl.DateTimeFormat("en-US", { timeZoneName: "long" }).formatToParts(date);
    const timeZoneName = timeZone.find((part) => part.type === "timeZoneName")?.value || "";
    const offset = -date.getTimezoneOffset();
    const hours = String(Math.floor(Math.abs(offset) / 60)).padStart(2, "0");
    const minutes = String(Math.abs(offset) % 60).padStart(2, "0");
    const sign = offset >= 0 ? "+" : "-";
    const formattedOffset = `(UTC${sign}${hours}:${minutes})`;

    return `${formattedOffset} ${timeZoneName}`;
  }
}
